// @flow
import { isString } from "voca";
import { uniqueId } from "lodash";
import type { AddToQueueType, PublicIdData, ExternalInterface } from "uploader";
import getLogger from "common/logger";
import { isFile } from "utils/fileUtils";
import t from "common/i18n";
import consts from "./common/consts";
import { EXT_EVENTS } from "./common/events";
import { types as ats } from "./store/actions";
import {
	selectRunningQueueFilesIds,
	selectUploads,
	selectActiveQueueFilesIds,
	selectQueues,
	selectHideData,
	selectPublicIdWithCounter,
	selectActiveQueueFolder,
} from "./store/selectors";
import { finalizeFileUpload } from "./fileUpload/uploadFileMethods";

type uploaderOptions = {
	actions: { [string]: Function },
	getState: Function,
	externalApi: ExternalInterface,
	sendExternalEvent: Function,
};

const UW_UPLOAD_FILE = "__uw_upload_file__";

const logger = getLogger();

export const getFilePathProps = (path: string) => {
	const splitFileAndFolderRegex = /^(.+[/])?([^/]+)$/;
	const regexGroups = splitFileAndFolderRegex.exec(path) || [];

	regexGroups.splice(0, 1);

	const [folder, filename] = regexGroups;

	return {
		name: filename,
		folder: folder && folder.slice(0, -1),
	};
};

export default (options: uploaderOptions) => {
	const { getState, actions } = options;

	const isAllowedToContinueWithClear = () => {
		const state = getState();
		const hideOptions = selectHideData(state).options;

		let isAllowed = true;

		if (!hideOptions || !hideOptions.quiet) {
			let runningIds = selectRunningQueueFilesIds(state);

			runningIds = (!runningIds || !runningIds.length)
				? selectActiveQueueFilesIds(state) : runningIds;

			if (runningIds && runningIds.length) {
				isAllowed = confirm(t("uploader.close_mid_upload"));
			}
		}

		return isAllowed;
	};

	const abortUploadsForIds = (ids: string[], files: { [string]: Object }, filesData: { [string]: Object }) => ids.map((id) => {
		let fileAbortPromise; // this will be a deferred object from jquery

		if (filesData[id] && !filesData[id].done) {
			const file = files[id];

			if (file) {
				logger.log(`[uploader.queuesManager]: aborting upload for file with id: ${id}`);
				fileAbortPromise = file.abortUpload && file.abortUpload();

				try {
					finalizeFileUpload(id, false, consts.ABORTED, options);
				} catch (ex) {
					logger.error(`[uploader.queuesManager]: finalize file upload with abort status failed! ${ex.message}`, ex);
				}
			}
		}

		let abortResult;

		if (fileAbortPromise) {
			const flowAbortResult = fileAbortPromise; // flow is stupid :(

			abortResult = new Promise((resolve) => // wrapping with real promise because flowAbortResult is a jquery deferred, not a real promise
				flowAbortResult
					.error((data) => {
						if (data && data.statusText !== "abort") {
							logger.log("[uploader.queuesManager]: upload abort error occurred - ", data.statusText);
						}

						resolve(); // need to manually resolve because flowAbortResult isnt a real promise. .then() wont be called after .error() ...
					})
					.then(resolve));
		} else {
			abortResult = Promise.resolve();
		}

		return abortResult;
	});

	const abortAll = () => {
		const queues = selectQueues(getState());
		let aborts = [];

		queues.forEach((queue) => {
			if (queue.status !== consts.QUEUE_STATUS.FINISHED) {
				const queueAborts = abortUploadsForIds(queue.ids, queue.files, queue.filesData); // cancel every file even if in non-active queue
				aborts = aborts.concat(queueAborts);
				options.actions[ats.SET_UPLOADER_QUEUE_FINISHED]({ id: queue.queueId }); // ensure queues status is set to finished
			}
		});

		options.sendExternalEvent({ event: EXT_EVENTS.ABORT, info: { ids: null, all: true } });

		return Promise.all(aborts);
	};

	const abortFiles = (ids: string[]) => {
		const uploads = selectUploads(getState());

		abortUploadsForIds(ids, uploads.files, uploads.filesData);
		options.sendExternalEvent({
			event: EXT_EVENTS.ABORT,
			info: { ids: ids.slice(), all: false }
		});
	};

	const clearQueues = async () => {
		const isAllowed = isAllowedToContinueWithClear();

		if (isAllowed) {
			logger.log("[uploader.queuesManager]: clear queues about to abort all active uploads");

			try {
				actions[ats.START_CLEAR_QUEUES]();
				await abortAll();
			} catch (ex) {
				logger.error(`[uploader.queuesManager]: encountered error while aborting files for clearQueues: ${ex.message}`, ex);
			}

			actions[ats.CLEAR_UPLOADER_QUEUES](); // clear all queues from store
			actions[ats.END_CLEAR_QUEUES](); // flag that were done with clearing queues
		}

		return isAllowed;
	};

	const isWildCardId = (ids: string[]) => ids.length === 1 && ids[0][0] === "*";

	const abortUploads = (ids: string[]) => {
		if (isWildCardId(ids)) {
			logger.log("[uploader.queuesManager]: received request to abort ALL uploads");
			abortAll();
		} else if (ids.length) {
			logger.log("[uploader.queuesManager]: received request to abort uploads with ids: ", ids);
			abortFiles(ids);
		}
	};

	const processRetries = (ids: string[]) => {
		const retryAll = isWildCardId(ids);

		if (retryAll) {
			logger.log("[uploader.queuesManager]: received request to retry ALL uploads");
		} else if (ids.length) {
			logger.log("[uploader.queuesManager]: received request to retry uploads with ids: ", ids);
		}

		options.sendExternalEvent({
			event: EXT_EVENTS.RETRY,
			info: {
				ids: (retryAll ? null : ids),
				all: retryAll,
			},
		});

		actions[ats.SET_UPLOADER_RETRY_FILES]({ ids, retryAll }); // dispatch action to remove data from store and reset upload files progress info
	};

	const getUploadFile = (fileData: Object) => ({
		...fileData,
		[UW_UPLOAD_FILE]: true,
	});

	const getPausedFilesFromQueue = (selector): Object[] => {
		let files = [];
		const queueFilesIds = selector(getState());

		if (queueFilesIds && queueFilesIds.length) {
			logger.log(`[uploader]: found [${queueFilesIds.length}] paused files from queue`);
			files = queueFilesIds.map((id) => getUploadFile({ id }));
		}

		return files;
	};

	const getPausedFilesFromRunningQueue = (): Object[] => getPausedFilesFromQueue(selectRunningQueueFilesIds);

	const getPausedFilesFromActiveQueue = (): Object[] => getPausedFilesFromQueue(selectActiveQueueFilesIds);

	const getFileCurrentPublicIdCounter = (publicId, lastPublicIdCounter) =>
		(publicId ?
			(lastPublicIdCounter > 2 ? (lastPublicIdCounter - 1) : 0) :
			// when no public id was used
			-1);

	const getUploadFolder = (configFolder: ?string, filePath: string) => {
		const fileFolder = getFilePathProps(filePath).folder;

		return fileFolder
			? configFolder ? `${configFolder}${fileFolder}` : fileFolder
			: configFolder;
	};

	const getBatchData = (filesForQueue: Object[]): Object[] => {
		const state = getState(); // must get the latest state because actions have
		const uploadFolder = selectActiveQueueFolder(state) || ""; // get the folder files are going to be uploaded to

		return filesForQueue.map((fileData: any) => {
			const filePath = fileData.file.hdcFullPath || fileData.file.name;
			return getUploadFile({
				id: fileData.id,
				name: isString(fileData.file) ? fileData.file : filePath,
				publicId: fileData.publicId,
				folder: filePath ? getUploadFolder(uploadFolder, filePath) : uploadFolder,
				customCoordinates: fileData.customCoordinates,
				headers: isString(fileData.file.file) ? fileData.file.headers: undefined,
				filenameOverride: isString(fileData.file.file) ? fileData.file.filenameOverride: undefined,
			});
		}); // return placeholder 'file' objects for callbacks to use
	};

	const sendExternalFileAdded = (id: string, batchId: string, publicId: string, file) => {
		options.sendExternalEvent({ // notify client as soon as files are added as part of a new batch
			event: EXT_EVENTS.FILE_ADDED,
			info: {
				id,
				batchId,
				publicId,
				file: isFile(file) ? {
					lastModified: file.lastModified,
					lastModifiedDate: file.lastModifiedDate,
					name: file.name,
					size: file.size,
					type: file.type,
					hdcFullPath: file.hdcFullPath,
				} : file,
			},
		});
	};

	const getFileCustomCoordinates = (data, fileIndex) => {
		let coordinates;

		if (data.cropInfo && data.cropInfo[fileIndex] && data.cropInfo[fileIndex].pixelCrop) {
			const cropData = data.cropInfo[fileIndex];

			if (cropData.width !== 100 || cropData.height !== 100) {
				coordinates = cropData.pixelCrop;
			}
		}

		return coordinates;
	};

	const getFileDimensions = (data, fileIndex) => {
		let dimensions = [];

		if (data.cropInfo && data.cropInfo[fileIndex] && data.cropInfo[fileIndex].pixelCrop) {
			const cropData = data.cropInfo[fileIndex];

			if (cropData.width !== 100 || cropData.height !== 100) {
				dimensions = cropData.dimensions;
			}
		}

		return dimensions;
	};

	const getFilesForQueue = (state: Object, data: AddToQueueType, publicIdData: ?PublicIdData, delayed: boolean) => {
		const batchId = uniqueId("uw-batch");
		const basePublicId = publicIdData && publicIdData.publicId;

		let lastPublicIdCounter = publicIdData?.counter || 2; // start from 2 as suffix for the next time this public id is used

		return data.files.map((file: any, index: number) => {
			const id = uniqueId("uw-file"); // generate a unique id for the file that will be how its referenced during the upload process
			let publicId = basePublicId || "";

			if (basePublicId && (index || (publicIdData && publicIdData.counter))) {
				// in case publicId is provided add counter from second file and on or if previous batch already used the same pid
				publicId = `${basePublicId}-${lastPublicIdCounter}`;
				lastPublicIdCounter += 1;
			}

			sendExternalFileAdded(id, batchId, publicId, file);

			return {
				id,
				file,
				partOfBatch: (data.files.length > 1),
				delayed,
				batchId,
				publicId,
				publicIdCounter: getFileCurrentPublicIdCounter(publicId, lastPublicIdCounter),
				customCoordinates: getFileCustomCoordinates(data, index),
				imageDimensions: getFileDimensions(data, index),
			};
		});
	};

	type AddNewBatchOptions = {| delayed: boolean, multiple: boolean, delayedPreCalls: boolean |};

	const addNewBatchData = (data: AddToQueueType, batchOptions: AddNewBatchOptions) => {
		const state = getState();
		const publicIdData = selectPublicIdWithCounter(state);

		if (!batchOptions.multiple && data.files.length > 1) {
			logger.log(`[uploader.queuesManager]: multiple uploads disabled. Taking first file from batch of: ${data.files.length}`);
			data.files = [data.files[0]]; // only accept one file if multiple is false
		}

		const filesForQueue = getFilesForQueue(state, data, publicIdData, batchOptions.delayed);

		options.actions[ats.SET_UPLOADER_FILES_QUEUED]({
			files: filesForQueue,
			...batchOptions,
			configOverride: data.config, // batch needs to lock the config (ex: client batch)
		});

		if (publicIdData && publicIdData.publicId) { // dispatch update to last public id counter
			const lastFileCounter = filesForQueue.slice(-1)[0].publicIdCounter;

			// make sure the public id counter is set to the next value in case next batch needs it
			options.actions[ats.SET_UPLOADER_PID_COUNTER]({
				publicId: publicIdData.publicId,
				counter: lastFileCounter > 0 ? (lastFileCounter + 1) : 2, // counter should be 2 for the second file and so on
			});
		}

		return getBatchData(filesForQueue);
	};

	return {
		getPausedFilesFromActiveQueue,
		getPausedFilesFromRunningQueue,
		clearQueues,
		abortUploads,
		processRetries,
		addNewBatchData,
	};
};
